import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import type { RowModel } from '@tanstack/react-table';
import type { PrioritizedProductListEntry } from 'contracts/prioritized-products';
import { useAuth } from 'hooks';
import {
  useFetchPrioritizedProducts,
  useUpdatePrioritizedProduct,
  useRevertPrioritizedProducts,
} from 'hooks/api/prioritized-products';
import { useCreateServiceOrder } from 'hooks/api/service-orders';
import { usePrioritizedProductsSearchParams } from 'hooks/store/prioritized-products';
import { useOffsetTop } from 'hooks/ui';
import { Scaffold } from 'layouts';
import { notify } from 'services';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { DataTable, type DataTableRef } from 'components/ui/DataTable';
import {
  ConfirmationDialog,
  type ConfirmationDialogRef,
} from 'components/ui/confirmation-dialog';

import { TabbedNavigation, Indicators } from '../shared-components';
import { usePrioritizedProductColumns } from './hooks';
import {
  PrioritizedProductsFilter,
  CreateServiceOrderDrawer,
  type CreateServiceOrderDrawerRef,
  UpdateProductPriorityDrawer,
  type UpdateProductPriorityDrawerRef,
} from './components';

const NAVIGATE_TO_SERVICE_ORDERS_PAGE_ON_SUCCESS = false;
const NAVIGATE_TO_BLOCKED_PRODUCTS_PAGE_ON_REVERT_SUCCESS = true;

export const PrioritizedProductsPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { filters, updateFilters, clearState } = usePrioritizedProductsSearchParams();
  const { fetchPrioritizedProducts } = useFetchPrioritizedProducts();
  const { createServiceOrder } = useCreateServiceOrder();
  const { updatePrioritizedProduct } = useUpdatePrioritizedProduct();
  const { revertPrioritizedProducts } = useRevertPrioritizedProducts();

  const datatableWrapperRef = React.useRef<HTMLDivElement>(null);
  const createServiceOrderDrawerRef = React.useRef<CreateServiceOrderDrawerRef>(null);
  const updateProductPriorityDrawerRef = React.useRef<UpdateProductPriorityDrawerRef>(null);
  const revertPrioritizedProductConfirmationDialogRef = React.useRef<ConfirmationDialogRef>(null);
  const dataTableRef = React.useRef<DataTableRef>(null);

  const datatableWrapperOffsetTop = useOffsetTop({
    ref: datatableWrapperRef,
    updateInterval: 1000,
  });

  const datatableWrapperStyle = React.useMemo((): React.CSSProperties => {
    return {
      minHeight: '100px',
      maxHeight: `calc(100vh - ${datatableWrapperOffsetTop + 150}px)`,
    };
  }, [datatableWrapperOffsetTop]);

  const isAdminOrWarehouseMember = React.useMemo(() => {
    if (!profile?.type) return false;

    return ['admin', 'warehouseMember'].includes(profile.type);
  }, [profile?.type]);

  const queryClient = useQueryClient();

  const productsQuery = useQuery({
    queryKey: ['fetch-prioritized-products', filters],
    queryFn: () => fetchPrioritizedProducts({ filters }),
    refetchOnWindowFocus: false,
  });

  const createServiceOrderMutation = useMutation({
    mutationFn: createServiceOrder,
    onSuccess: async () => {
      dataTableRef.current?.clearSelection();
      await queryClient.invalidateQueries(['fetch-prioritized-products']);
      notify('success', 'Ordem de Serviço criada com sucesso!');
      if (NAVIGATE_TO_SERVICE_ORDERS_PAGE_ON_SUCCESS) {
        navigate('/inspecao-estoque/ordens-servico');
      }
    },
    onError: () => {
      notify('error', 'Erro ao criar ordem de serviço.');
    },
    onSettled: () => {
      createServiceOrderDrawerRef.current?.close();
    },
  });

  const updateProductMutation = useMutation({
    mutationFn: updatePrioritizedProduct,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-prioritized-products']);
      notify('success', 'Prioridade do produto atualizada com sucesso!');
    },
    onError: () => {
      notify('error', 'Erro ao atualizar prioridade do produto.');
    },
    onSettled: () => {
      updateProductPriorityDrawerRef.current?.close();
    },
  });

  const revertProductMutation = useMutation({
    mutationFn: revertPrioritizedProducts,
    onSuccess: async () => {
      notify('success', 'Prioridade do produto cancelada com sucesso!');
      if (NAVIGATE_TO_BLOCKED_PRODUCTS_PAGE_ON_REVERT_SUCCESS) {
        navigate('/inspecao-estoque/produtos-bloqueados');
      }
    },
    onError: () => {
      notify('error', 'Erro ao cancelar prioridade do produto.');
    },
  });

  const onSelectRows = React.useCallback((rowModel: RowModel<PrioritizedProductListEntry>) => {
    const selectedProducts = rowModel.flatRows.map(i => i.original);
    const rowIdentifiers = selectedProducts.map(i => {
      return [i.plant, i.destinationPlant, i.storageLocation, i.material].filter(Boolean).join(':');
    });
    // @ts-ignore
    const uniqueRowIdentifiers: string[] = [...new Set(rowIdentifiers)];
    if (uniqueRowIdentifiers.length > 1) {
      const message = `A seleção deve conter apenas produtos com mesma planta, planta de destino, local de armazenamento (SLOC) e código.`;
      notify('warning', message);
    } else {
      createServiceOrderDrawerRef.current?.open(selectedProducts);
    }
  }, []);

  const openRevertPrioritizedProduct = React.useCallback(async (selectedProduct: PrioritizedProductListEntry) => {
    const message = (
      <div className={'space-y-2'}>
        <p>Confirmar cancelamento de prioridade to produto?</p>
        <p className={'font-GilroySemibold'}>{selectedProduct.materialDescription}</p>
      </div>
    );

    const confirmation = await revertPrioritizedProductConfirmationDialogRef
      .current?.openDialog({ title: 'Atenção', message });

    if (confirmation !== 'confirmed') return;

    revertProductMutation.mutate({
      productIds: [selectedProduct.id],
    });
  }, []);

  const columns = usePrioritizedProductColumns({
    onUpdatePriority: updateProductPriorityDrawerRef.current?.open,
    onRevertPrioritizedProduct: openRevertPrioritizedProduct,
    showSelectionColumn: isAdminOrWarehouseMember,
  });

  return (
    <Scaffold>
      <TabbedNavigation />
      <PageHeaderForFilters headerTitle={
        <div className={'w-full font-GilroySemibold'}>Filtro</div>
      } className={'mb-4'}>
        <PrioritizedProductsFilter
          onSubmitFilters={updateFilters}
          onResetFilters={clearState}
        />
      </PageHeaderForFilters>
      <Indicators
        indicators={productsQuery?.data?.indicators}
        loading={productsQuery.isLoading}
        className={'mb-4'}
      />
      <CreateServiceOrderDrawer
        ref={createServiceOrderDrawerRef}
        onSubmit={createServiceOrderMutation.mutate}
        loading={createServiceOrderMutation.isLoading}
      />
      <UpdateProductPriorityDrawer
        ref={updateProductPriorityDrawerRef}
        onSubmit={updateProductMutation.mutate}
        loading={updateProductMutation.isLoading}
      />
      <ConfirmationDialog
        ref={revertPrioritizedProductConfirmationDialogRef}
      />
      <div ref={datatableWrapperRef}>
        <DataTable
          ref={dataTableRef}
          data={productsQuery?.data?.prioritizedProducts ?? []}
          isLoading={productsQuery.isLoading}
          columns={columns}
          tableWrapperStyle={datatableWrapperStyle}
          leftPinnedColumns={['Selection', 'Prior.', 'Planta']}
          rightPinnedColumns={['Actions']}
          selectionAction={isAdminOrWarehouseMember ? onSelectRows : undefined}
          selectionActionLabel={'Criar OS'}
        />
      </div>
    </Scaffold>
  );
};

